import { Input, Modal } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

import TeamPocFields from '@/components/TeamPocFields';

import { INSIGHTS_DESK_TEAM_OPTIONS } from '@/constants/project.enum';

import { useProjectPermissions } from '@/hooks/usePermission';

import { ScreeningQuestion } from '@/interface';

import { ScreeningQuestionsBuilder } from '@/pages/dashboard/project/components/ScreeningQuestionsBuilder';
import { useScreeningQuestionsForm } from '@/pages/dashboard/project/components/useScreeningQuestionsForm';

import { getMembersRequest } from '@/redux/slices';
import { AppDispatch, RootState } from '@/redux/store';

interface EditProjectModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  requestedCalls?: number;
  insightsDeskTeam?: string | null;
  insightsDeskPocNames?: string[] | null;
  insightsDeskPocDisplayNames?: string[] | null;
  screeningQuestions?: ScreeningQuestion[];
  onSave: (
    title: string,
    description: string,
    requestedCalls: number,
    insightsDeskTeam: string,
    insightsDeskPocNames: string[],
    screeningQuestions: ScreeningQuestion[],
  ) => void;
}

export default function EditProjectModal({
  open,
  onClose,
  title,
  description,
  requestedCalls = 0,
  insightsDeskTeam = '',
  insightsDeskPocNames = [],
  insightsDeskPocDisplayNames = [],
  screeningQuestions = [],
  onSave,
}: EditProjectModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { companyId } = useSelector((state: RootState) => state.auth);
  const { members } = useSelector((state: RootState) => state.members);
  const hasPermission = useProjectPermissions();
  const canViewScreening = hasPermission('view_screening_answers');
  const canAddScreening = hasPermission('add_screening_answers');
  const canUpdateScreening = hasPermission('update_screening_answers');
  const canDeleteScreening = hasPermission('delete_screening_answers');
  const canMutateScreening = canAddScreening || canUpdateScreening || canDeleteScreening;
  const selectedPocDisplayNameByValue = useMemo(() => {
    const valueLabelPairs = (insightsDeskPocNames || []).map((value, index) => {
      const normalizedValue = String(value || '').trim();
      const displayName = String(insightsDeskPocDisplayNames?.[index] || '').trim();
      return [normalizedValue, displayName] as const;
    });

    return new Map(valueLabelPairs.filter(([value, displayName]) => value && displayName));
  }, [insightsDeskPocDisplayNames, insightsDeskPocNames]);
  const [newTitle, setNewTitle] = useState(title);
  const [newDesc, setNewDesc] = useState(description);
  const [newRequestedCalls, setNewRequestedCalls] = useState(String(requestedCalls || 0));
  const [newInsightsDeskTeam, setNewInsightsDeskTeam] = useState(insightsDeskTeam || undefined);
  const [newInsightsDeskPocNames, setNewInsightsDeskPocNames] = useState<string[]>(
    insightsDeskPocNames?.length ? insightsDeskPocNames : [''],
  );
  const [titleError, setTitleError] = useState(false);
  const [descError, setDescError] = useState(false);
  const [requestedCallsError, setRequestedCallsError] = useState(false);
  const [insightsDeskTeamError, setInsightsDeskTeamError] = useState(false);
  const [insightsDeskPocError, setInsightsDeskPocError] = useState(false);
  const {
    screeningQuestions: questions,
    setScreeningQuestions,
    questionValidationError,
    resetScreeningQuestions,
    addScreeningQuestion,
    updateScreeningQuestion,
    removeScreeningQuestion,
    addQuestionOption,
    updateQuestionOption,
    removeQuestionOption,
    addQuestionStatement,
    updateQuestionStatement,
    removeQuestionStatement,
    validateScreeningQuestions,
    normalizeScreeningQuestions,
  } = useScreeningQuestionsForm();
  const teamLeadPocOptions = useMemo(() => {
    const teamLeadMembers = members.filter((member) => member.roleName?.toLowerCase().trim() === 'team lead / manager');
    const options = teamLeadMembers
      .map((member) => {
        const username = member.userName?.trim();
        const email = member.userEmail?.trim();
        const label = username && email ? `${username} (${email})` : username || email;
        if (!label || !member.userId) return null;
        return {
          label,
          value: `user:${member.userId}`,
        };
      })
      .filter((option): option is { label: string; value: string } => Boolean(option));
    const optionByValue = new Map(options.map((option) => [option.value, option]));

    newInsightsDeskPocNames
      .map((name) => name.trim())
      .filter(Boolean)
      .forEach((name) => {
        const label = selectedPocDisplayNameByValue.get(name) || name;
        if (!optionByValue.has(name)) optionByValue.set(name, { label, value: name });
      });

    return Array.from(optionByValue.values());
  }, [members, newInsightsDeskPocNames, selectedPocDisplayNameByValue]);

  useEffect(() => {
    if (open && companyId) {
      dispatch(getMembersRequest(companyId));
    }
  }, [companyId, dispatch, open]);

  useEffect(() => {
    if (open) {
      setNewTitle(title);
      setNewDesc(description);
      setNewRequestedCalls(String(requestedCalls || 0));
      setNewInsightsDeskTeam(insightsDeskTeam || undefined);
      setNewInsightsDeskPocNames(insightsDeskPocNames?.length ? insightsDeskPocNames : ['']);
      resetScreeningQuestions(screeningQuestions);
      setTitleError(false);
      setDescError(false);
      setRequestedCallsError(false);
      setInsightsDeskTeamError(false);
      setInsightsDeskPocError(false);
    }
  }, [
    open,
    title,
    description,
    requestedCalls,
    insightsDeskTeam,
    insightsDeskPocNames,
    screeningQuestions,
    resetScreeningQuestions,
  ]);

  const handlePocNameChange = (index: number, value: string) => {
    setNewInsightsDeskPocNames((prev) => (index === 0 ? [value] : prev));
    setInsightsDeskPocError(false);
  };

  const handleAddPocName = () => {
    setNewInsightsDeskPocNames((prev) => [...prev, '']);
  };

  const handleRemovePocName = (index: number) => {
    setNewInsightsDeskPocNames((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return next.length ? next : [''];
    });
  };

  const scrollToScreeningField = (target: {
    message: string;
    questionIndex: number;
    field: 'question' | 'option' | 'statement';
    itemIndex?: number;
  }) => {
    const fieldKey =
      target.field === 'question'
        ? `edit-question-${target.questionIndex}-question`
        : `edit-question-${target.questionIndex}-${target.field}-${target.itemIndex ?? 0}`;
    const fieldElement = document.querySelector<HTMLElement>(`[data-screening-field="${fieldKey}"]`);
    fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => fieldElement?.focus(), 250);
  };

  const handleSubmit = () => {
    const isTitleEmpty = !newTitle.trim();
    const isDescEmpty = !newDesc.trim();
    const parsedRequestedCalls = Number(newRequestedCalls);
    const isRequestedCallsInvalid = !newRequestedCalls.trim() || Number.isNaN(parsedRequestedCalls) || parsedRequestedCalls < 1;
    const normalizedPocNames = newInsightsDeskPocNames.map((name) => name.trim()).filter(Boolean);
    const isInsightsDeskTeamEmpty = !newInsightsDeskTeam;
    const isInsightsDeskPocEmpty = normalizedPocNames.length === 0;

    setTitleError(isTitleEmpty);
    setDescError(isDescEmpty);
    setRequestedCallsError(isRequestedCallsInvalid);
    setInsightsDeskTeamError(isInsightsDeskTeamEmpty);
    setInsightsDeskPocError(isInsightsDeskPocEmpty);

    if (isTitleEmpty || isDescEmpty || isRequestedCallsInvalid || isInsightsDeskTeamEmpty || isInsightsDeskPocEmpty) return;

    if (canMutateScreening) {
      const screeningValidationError = validateScreeningQuestions();
      if (screeningValidationError) {
        toast.error(screeningValidationError.message);
        scrollToScreeningField(screeningValidationError);
        return;
      }
    }

    const normalizedQuestions = normalizeScreeningQuestions();

    onSave(newTitle.trim(), newDesc.trim(), parsedRequestedCalls, newInsightsDeskTeam, normalizedPocNames, normalizedQuestions);
  };

  return (
    <Modal
      open={open}
      title="Edit Project"
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Save"
      cancelText="Cancel"
      centered
      width="min(900px, calc(100vw - 32px))"
    >
      <div className="space-y-4 mt-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <Input
            placeholder="Enter project title"
            value={newTitle}
            onChange={(e) => {
              setNewTitle(e.target.value);
              setTitleError(false);
            }}
            className={titleError ? 'border-red-500' : ''}
          />
          {titleError && <div className="text-red-500 text-xs mt-1">Title is required</div>}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <Input.TextArea
            placeholder="Enter project description"
            rows={4}
            value={newDesc}
            onChange={(e) => {
              setNewDesc(e.target.value);
              setDescError(false);
            }}
            className={descError ? 'border-red-500' : ''}
          />
          {descError && <div className="text-red-500 text-xs mt-1">Description is required</div>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Requested Number of Calls</label>
          <Input
            type="number"
            min={1}
            placeholder="Enter requested number of calls"
            value={newRequestedCalls}
            onChange={(e) => {
              setNewRequestedCalls(e.target.value);
              setRequestedCallsError(false);
            }}
            className={requestedCallsError ? 'border-red-500' : ''}
          />
          {requestedCallsError && <div className="text-red-500 text-xs mt-1">Requested calls must be at least 1</div>}
        </div>

        <TeamPocFields
          team={newInsightsDeskTeam}
          pocNames={newInsightsDeskPocNames}
          teamOptions={INSIGHTS_DESK_TEAM_OPTIONS}
          pocOptions={teamLeadPocOptions}
          singlePocOnly
          teamError={insightsDeskTeamError}
          pocError={insightsDeskPocError}
          teamLabel="Insights Desk Team"
          teamPlaceholder="Select Insights Desk Team"
          teamRequiredMessage="Insights Desk Team is required"
          pocLabel="Insights Desk POC Name"
          pocPlaceholderPrefix="Enter POC name"
          pocRequiredMessage="At least one Insights Desk POC name is required"
          removePocAriaLabelPrefix="Remove Insights Desk POC"
          teamSelectClassName={`w-full ${insightsDeskTeamError ? 'border border-red-500 rounded-md' : ''}`}
          pocInputClassName={insightsDeskPocError ? 'border-red-500' : ''}
          onTeamChange={(value) => {
            setNewInsightsDeskTeam(value);
            setInsightsDeskTeamError(false);
          }}
          onPocNameChange={handlePocNameChange}
          onAddPocName={handleAddPocName}
          onRemovePocName={handleRemovePocName}
        />

        {canViewScreening ? (
          <div className="border-t pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-sm font-medium text-gray-800">Screening Questions (Segments)</div>
                <div className="text-xs text-gray-500 font-normal font-sans">Create segments and add questions under them.</div>
              </div>
            </div>

            <ScreeningQuestionsBuilder
              questions={questions}
              setScreeningQuestions={setScreeningQuestions}
              canAdd={canAddScreening}
              canUpdate={canUpdateScreening}
              canDelete={canDeleteScreening}
              addScreeningQuestion={addScreeningQuestion}
              updateScreeningQuestion={updateScreeningQuestion}
              removeScreeningQuestion={removeScreeningQuestion}
              addQuestionOption={addQuestionOption}
              updateQuestionOption={updateQuestionOption}
              removeQuestionOption={removeQuestionOption}
              addQuestionStatement={addQuestionStatement}
              updateQuestionStatement={updateQuestionStatement}
              removeQuestionStatement={removeQuestionStatement}
              questionValidationError={questionValidationError}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
